import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { CalendarPlus, CalendarX2, Repeat, SlidersHorizontal, Users2 } from 'lucide-react';
import { merchantExperienceApi, extractErrorMessage, formatNumber } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type { MerchantExperienceSessionDto, WeekdayCode } from '@kardume/types';
import {
  EXPERIENCE_RECURRENCE_MAX_SESSIONS,
  EXPERIENCE_SESSION_STATUS_LABELS,
  MERCHANT_EXPERIENCE_ERROR_MESSAGES,
} from '@kardume/types';
import {
  ActionButton,
  Badge,
  Button,
  Checkbox,
  Modal,
  Table,
  TableContainer,
  TextInput,
} from '@kardume/ui';
import {
  RECURRENCE_WEEKDAYS,
  endsNextDay,
  formatSessionRange,
  isPast,
  toEndInstant,
  toInstant,
  todayIso,
} from '../../utils/experienceSchedule';

/**
 * A agenda da experiência: datas, vagas e fila de espera (card #347).
 *
 * ## O que a tela precisa dizer sobre vaga
 *
 * `capacity`, `reservedCount`, `availableSeats` e `waitlistCount` são quatro
 * números diferentes e cada um responde a uma pergunta da noite. Mostrá-los
 * juntos é o ponto da tela - a loja decide arrumar mesa a mais olhando para a
 * fila de espera, não para a lotação.
 *
 * ## Cancelar não é o mesmo que reduzir vaga
 *
 * Reduzir abaixo do já reservado é recusado pelo core, e a mensagem dele traz o
 * piso; a tela mostra essa mensagem em vez de uma genérica. **Aumentar** promove
 * a fila de espera na hora, então a agenda inteira é relida depois - os números
 * mudam sem que ninguém tenha clicado neles.
 *
 * ## Cancelar avisa gente de verdade
 *
 * Cancelar uma data cancela as reservas vivas dela (confirmadas e de fila de
 * espera). A confirmação diz quantas pessoas serão avisadas, porque esse é o
 * custo real do clique.
 */

const Wrapper = styled.div`
  display: grid;
  gap: 1rem;
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const Seats = styled.div`
  font-size: 0.82rem;
  color: ${(p) => p.theme.textSecondary};
  white-space: nowrap;

  span {
    display: block;
    font-size: 0.74rem;
    color: ${(p) => p.theme.textMuted};
  }
`;

const Waitlist = styled.span<{ $any: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.82rem;
  font-weight: ${(p) => (p.$any ? 700 : 400)};
  color: ${(p) => (p.$any ? p.theme.accent : p.theme.textMuted)};
  white-space: nowrap;
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.4rem;
  justify-content: flex-end;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 0.85rem;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FieldLabel = styled.span`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
  color: ${(p) => p.theme.textSecondary};
`;

const Weekdays = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding: 0.7rem 0.85rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
`;

const HelpText = styled.p`
  margin: 0.3rem 0 0;
  font-size: 0.75rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textMuted};
`;

const ConfirmText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.55;
  color: ${(p) => p.theme.textSecondary};

  strong {
    color: ${(p) => p.theme.textPrimary};
  }

  & + & {
    margin-top: 0.75rem;
  }
`;

const Empty = styled.div`
  padding: 1.5rem;
  text-align: center;
  font-size: 0.88rem;
  line-height: 1.55;
  color: ${(p) => p.theme.textMuted};
  border: 1px dashed ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
`;

const DEFAULT_CAPACITY = 10;

interface SingleForm {
  date: string;
  startTime: string;
  endTime: string;
  capacity: string;
}

interface RecurrenceForm {
  daysOfWeek: WeekdayCode[];
  startTime: string;
  durationMinutes: string;
  from: string;
  until: string;
  capacity: string;
}

const EMPTY_SINGLE: SingleForm = { date: '', startTime: '20:00', endTime: '23:00', capacity: String(DEFAULT_CAPACITY) };

const EMPTY_RECURRENCE: RecurrenceForm = {
  daysOfWeek: [],
  startTime: '20:00',
  durationMinutes: '180',
  from: '',
  until: '',
  capacity: String(DEFAULT_CAPACITY),
};

interface Props {
  experienceId: string;
  sessions: MerchantExperienceSessionDto[];
  canManage: boolean;
  onChanged: () => Promise<void> | void;
  /** Card #348 - abre a lista de participantes da data. */
  onOpenParticipants?: (session: MerchantExperienceSessionDto) => void;
}

const ExperienceSessionsPanel: React.FC<Props> = ({
  experienceId,
  sessions,
  canManage,
  onChanged,
  onOpenParticipants,
}) => {
  const { showNotification } = useNotification();

  const [singleOpen, setSingleOpen] = useState(false);
  const [single, setSingle] = useState<SingleForm>(EMPTY_SINGLE);
  const [recurrenceOpen, setRecurrenceOpen] = useState(false);
  const [recurrence, setRecurrence] = useState<RecurrenceForm>(EMPTY_RECURRENCE);
  const [capacityTarget, setCapacityTarget] = useState<MerchantExperienceSessionDto | null>(null);
  const [capacityValue, setCapacityValue] = useState('');
  const [cancelTarget, setCancelTarget] = useState<MerchantExperienceSessionDto | null>(null);
  const [cancelSeries, setCancelSeries] = useState<MerchantExperienceSessionDto | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const ordered = useMemo(
    () => [...sessions].sort((a, b) => a.startsAt.localeCompare(b.startsAt)),
    [sessions]
  );

  /** Datas futuras da mesma série - é o que "cancelar a série" atinge. */
  const seriesSessions = useMemo(() => {
    if (!cancelSeries?.recurrenceGroupId) return [];
    return ordered.filter(
      (session) =>
        session.recurrenceGroupId === cancelSeries.recurrenceGroupId &&
        session.status === 'SCHEDULED' &&
        !isPast(session.startsAt)
    );
  }, [ordered, cancelSeries]);

  const openSingle = (): void => {
    setSingle({ ...EMPTY_SINGLE, date: todayIso() });
    setSingleOpen(true);
  };

  const openRecurrence = (): void => {
    setRecurrence({ ...EMPTY_RECURRENCE, from: todayIso(), until: '' });
    setRecurrenceOpen(true);
  };

  const createSingle = async (): Promise<void> => {
    if (!single.date || !single.startTime || !single.endTime) {
      showNotification('Informe a data, o horário de início e o de término.', 'error');
      return;
    }
    const capacity = Number(single.capacity);
    if (!Number.isFinite(capacity) || capacity < 1) {
      showNotification('A data precisa oferecer pelo menos uma vaga.', 'error');
      return;
    }
    setSubmitting(true);
    try {
      await merchantExperienceApi.addSession(experienceId, {
        startsAt: toInstant(single.date, single.startTime),
        endsAt: toEndInstant(single.date, single.startTime, single.endTime),
        capacity,
      });
      showNotification('Data aberta na agenda.', 'success');
      setSingleOpen(false);
      await onChanged();
    } catch (err) {
      showNotification(
        extractErrorMessage(err, 'Não foi possível abrir a data.', MERCHANT_EXPERIENCE_ERROR_MESSAGES),
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const createRecurrence = async (): Promise<void> => {
    if (recurrence.daysOfWeek.length === 0) {
      showNotification('Escolha ao menos um dia da semana.', 'error');
      return;
    }
    if (!recurrence.from || !recurrence.until) {
      showNotification('Informe o período da agenda (de e até).', 'error');
      return;
    }
    const capacity = Number(recurrence.capacity);
    const duration = Number(recurrence.durationMinutes);
    if (!Number.isFinite(capacity) || capacity < 1) {
      showNotification('Cada data precisa oferecer pelo menos uma vaga.', 'error');
      return;
    }
    if (!Number.isFinite(duration) || duration < 1) {
      showNotification('Informe a duração de cada data, em minutos.', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const result = await merchantExperienceApi.addRecurrence(experienceId, {
        daysOfWeek: recurrence.daysOfWeek,
        startsAtTime: recurrence.startTime,
        durationMinutes: duration,
        from: recurrence.from,
        until: recurrence.until,
        capacity,
      });
      // `created` é o que o servidor realmente gravou: datas que colidiam com o
      // que já existia foram puladas em silêncio, e anunciar o número pedido
      // faria a tela mentir.
      showNotification(
        result.created === 1
          ? '1 data criada na agenda.'
          : `${formatNumber(result.created)} datas criadas na agenda.`,
        'success'
      );
      setRecurrenceOpen(false);
      await onChanged();
    } catch (err) {
      showNotification(
        extractErrorMessage(
          err,
          'Não foi possível abrir a agenda recorrente.',
          MERCHANT_EXPERIENCE_ERROR_MESSAGES
        ),
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const openCapacity = (session: MerchantExperienceSessionDto): void => {
    setCapacityTarget(session);
    setCapacityValue(String(session.capacity));
  };

  const saveCapacity = async (): Promise<void> => {
    if (!capacityTarget) return;
    const capacity = Number(capacityValue);
    if (!Number.isFinite(capacity) || capacity < 1) {
      showNotification('A data precisa oferecer pelo menos uma vaga.', 'error');
      return;
    }
    setSubmitting(true);
    try {
      await merchantExperienceApi.changeSessionCapacity(capacityTarget.id, capacity);
      showNotification('Limite de vagas atualizado.', 'success');
      setCapacityTarget(null);
      // Aumentar promove a fila de espera na hora: sem reler, a tela mostraria
      // a fila antiga ao lado do limite novo.
      await onChanged();
    } catch (err) {
      // Sem mapa de mensagens: em EXPERIENCE_CAPACITY_BELOW_RESERVED o texto do
      // core traz o piso ("já existem 12 lugares ocupados"), que é o número que
      // o lojista precisa ler.
      showNotification(extractErrorMessage(err, 'Não foi possível mudar o limite de vagas.'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmCancelSession = async (): Promise<void> => {
    if (!cancelTarget) return;
    setSubmitting(true);
    try {
      await merchantExperienceApi.cancelSession(cancelTarget.id);
      showNotification('Data cancelada. Quem tinha reserva foi avisado.', 'success');
      setCancelTarget(null);
      await onChanged();
    } catch (err) {
      showNotification(
        extractErrorMessage(err, 'Não foi possível cancelar a data.', MERCHANT_EXPERIENCE_ERROR_MESSAGES),
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const confirmCancelSeries = async (): Promise<void> => {
    const groupId = cancelSeries?.recurrenceGroupId;
    if (!groupId) return;
    setSubmitting(true);
    try {
      const canceled = await merchantExperienceApi.cancelRecurrence(groupId);
      showNotification(
        canceled === 1 ? '1 data da série foi cancelada.' : `${formatNumber(canceled)} datas da série foram canceladas.`,
        'success'
      );
      setCancelSeries(null);
      await onChanged();
    } catch (err) {
      showNotification(
        extractErrorMessage(err, 'Não foi possível cancelar a série.', MERCHANT_EXPERIENCE_ERROR_MESSAGES),
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const toggleWeekday = (code: WeekdayCode): void =>
    setRecurrence((prev) => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(code)
        ? prev.daysOfWeek.filter((day) => day !== code)
        : [...prev.daysOfWeek, code],
    }));

  const affectedPeople = (session: MerchantExperienceSessionDto | null): number =>
    session ? session.reservedCount + session.waitlistCount : 0;

  const seriesAffected = seriesSessions.reduce(
    (total, session) => total + session.reservedCount + session.waitlistCount,
    0
  );

  return (
    <Wrapper>
      {canManage && (
        <Toolbar>
          <Button variant="primary" onClick={openSingle}>
            <CalendarPlus size={16} /> Abrir data
          </Button>
          <Button variant="outline" onClick={openRecurrence}>
            <Repeat size={16} /> Agenda recorrente
          </Button>
        </Toolbar>
      )}

      {ordered.length === 0 ? (
        <Empty>
          Nenhuma data marcada. <strong>Sem data futura a experiência não aparece na vitrine</strong>,
          mesmo aprovada pelo Kardume.
        </Empty>
      ) : (
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Data</th>
                <th>Vagas</th>
                <th>Fila de espera</th>
                <th>Presenças</th>
                <th>Situação</th>
                <th style={{ textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {ordered.map((session) => {
                const past = isPast(session.startsAt);
                const canceled = session.status === 'CANCELED';
                return (
                  <tr key={session.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {formatSessionRange(session.startsAt, session.endsAt)}
                      {session.recurrenceGroupId && (
                        <div>
                          <Badge tone="secondary">Série</Badge>
                        </div>
                      )}
                    </td>
                    <td>
                      <Seats>
                        {formatNumber(session.reservedCount)} de {formatNumber(session.capacity)}{' '}
                        ocupadas
                        <span>
                          {session.full
                            ? 'Lotada'
                            : `${formatNumber(session.availableSeats)} livres`}
                        </span>
                      </Seats>
                    </td>
                    <td>
                      <Waitlist $any={session.waitlistCount > 0}>
                        {session.waitlistCount > 0 && <Users2 size={14} />}
                        {formatNumber(session.waitlistCount)}
                      </Waitlist>
                    </td>
                    <td>{formatNumber(session.checkedInCount)}</td>
                    <td>
                      <Badge tone={canceled ? 'danger' : past ? 'secondary' : 'success'}>
                        {canceled
                          ? EXPERIENCE_SESSION_STATUS_LABELS.CANCELED
                          : past
                            ? 'Realizada'
                            : EXPERIENCE_SESSION_STATUS_LABELS.SCHEDULED}
                      </Badge>
                    </td>
                    <td>
                      <RowActions>
                        {onOpenParticipants && (
                          <ActionButton
                            aria-label="Ver participantes"
                            title="Participantes desta data"
                            onClick={() => onOpenParticipants(session)}
                          >
                            <Users2 size={16} />
                          </ActionButton>
                        )}
                        {canManage && !canceled && (
                          <>
                            <ActionButton
                              aria-label="Mudar o limite de vagas"
                              title="Mudar o limite de vagas"
                              onClick={() => openCapacity(session)}
                            >
                              <SlidersHorizontal size={16} />
                            </ActionButton>
                            <ActionButton
                              aria-label="Cancelar data"
                              title="Cancelar esta data"
                              color="#DC2626"
                              onClick={() => setCancelTarget(session)}
                            >
                              <CalendarX2 size={16} />
                            </ActionButton>
                            {session.recurrenceGroupId && !past && (
                              <ActionButton
                                aria-label="Cancelar a série inteira"
                                title="Cancelar a série inteira (só as datas futuras)"
                                color="#DC2626"
                                onClick={() => setCancelSeries(session)}
                              >
                                <Repeat size={16} />
                              </ActionButton>
                            )}
                          </>
                        )}
                      </RowActions>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableContainer>
      )}

      {/* ------------------------------------------------------ data avulsa */}
      <Modal
        isOpen={singleOpen}
        onClose={() => setSingleOpen(false)}
        title="Abrir uma data"
        width="560px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setSingleOpen(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={createSingle} isLoading={submitting}>
              Abrir data
            </Button>
          </>
        }
      >
        <FormGrid>
          <TextInput
            label="Dia"
            type="date"
            value={single.date}
            min={todayIso()}
            onChange={(e) => setSingle((prev) => ({ ...prev, date: e.target.value }))}
          />
          <Row>
            <TextInput
              label="Começa às"
              type="time"
              value={single.startTime}
              onChange={(e) => setSingle((prev) => ({ ...prev, startTime: e.target.value }))}
            />
            <TextInput
              label="Termina às"
              type="time"
              value={single.endTime}
              onChange={(e) => setSingle((prev) => ({ ...prev, endTime: e.target.value }))}
            />
          </Row>
          {endsNextDay(single.startTime, single.endTime) && (
            <HelpText>
              O término é no dia seguinte - uma noite que vira a madrugada é aceita normalmente.
            </HelpText>
          )}
          <div>
            <TextInput
              label="Vagas"
              type="number"
              min={1}
              value={single.capacity}
              onChange={(e) => setSingle((prev) => ({ ...prev, capacity: e.target.value }))}
            />
            <HelpText>
              Quantas pessoas cabem nesta data. Quem chegar depois de lotar entra na fila de espera.
            </HelpText>
          </div>
        </FormGrid>
      </Modal>

      {/* --------------------------------------------------- agenda recorrente */}
      <Modal
        isOpen={recurrenceOpen}
        onClose={() => setRecurrenceOpen(false)}
        title="Agenda recorrente"
        width="620px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRecurrenceOpen(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={createRecurrence} isLoading={submitting}>
              Gerar datas
            </Button>
          </>
        }
      >
        <FormGrid>
          <div>
            <FieldLabel>Dias da semana</FieldLabel>
            <Weekdays>
              {RECURRENCE_WEEKDAYS.map((day) => (
                <Checkbox
                  key={day.code}
                  label={day.label}
                  checked={recurrence.daysOfWeek.includes(day.code)}
                  onChange={() => toggleWeekday(day.code)}
                />
              ))}
            </Weekdays>
          </div>
          <Row>
            <TextInput
              label="Começa às"
              type="time"
              value={recurrence.startTime}
              onChange={(e) => setRecurrence((prev) => ({ ...prev, startTime: e.target.value }))}
            />
            <TextInput
              label="Duração (minutos)"
              type="number"
              min={1}
              max={1440}
              value={recurrence.durationMinutes}
              onChange={(e) =>
                setRecurrence((prev) => ({ ...prev, durationMinutes: e.target.value }))
              }
            />
          </Row>
          <Row>
            <TextInput
              label="De"
              type="date"
              value={recurrence.from}
              min={todayIso()}
              onChange={(e) => setRecurrence((prev) => ({ ...prev, from: e.target.value }))}
            />
            <TextInput
              label="Até"
              type="date"
              value={recurrence.until}
              min={recurrence.from || todayIso()}
              onChange={(e) => setRecurrence((prev) => ({ ...prev, until: e.target.value }))}
            />
          </Row>
          <div>
            <TextInput
              label="Vagas por data"
              type="number"
              min={1}
              value={recurrence.capacity}
              onChange={(e) => setRecurrence((prev) => ({ ...prev, capacity: e.target.value }))}
            />
            <HelpText>
              A agenda é gerada para no máximo um ano e {EXPERIENCE_RECURRENCE_MAX_SESSIONS} datas de
              cada vez. Datas que já existem na agenda são puladas - no fim, a tela avisa quantas
              foram realmente criadas.
            </HelpText>
          </div>
        </FormGrid>
      </Modal>

      {/* ---------------------------------------------------------- vagas */}
      <Modal
        isOpen={capacityTarget !== null}
        onClose={() => setCapacityTarget(null)}
        title="Mudar o limite de vagas"
        width="480px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCapacityTarget(null)} disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={saveCapacity} isLoading={submitting}>
              Salvar
            </Button>
          </>
        }
      >
        {capacityTarget && (
          <FormGrid>
            <ConfirmText>
              {formatSessionRange(capacityTarget.startsAt, capacityTarget.endsAt)} -{' '}
              <strong>{formatNumber(capacityTarget.reservedCount)}</strong> lugares já ocupados
              {capacityTarget.waitlistCount > 0 && (
                <>
                  {' '}
                  e <strong>{formatNumber(capacityTarget.waitlistCount)}</strong> pessoas na fila de
                  espera
                </>
              )}
              .
            </ConfirmText>
            <TextInput
              label="Vagas"
              type="number"
              min={1}
              value={capacityValue}
              onChange={(e) => setCapacityValue(e.target.value)}
            />
            <HelpText>
              Não dá para descer abaixo do que já está ocupado - alguém perderia o lugar, e escolher
              quem não é decisão do sistema. Aumentar promove a fila de espera na hora.
            </HelpText>
          </FormGrid>
        )}
      </Modal>

      {/* ------------------------------------------------- cancelar a data */}
      <Modal
        isOpen={cancelTarget !== null}
        onClose={() => setCancelTarget(null)}
        title="Cancelar esta data"
        width="520px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCancelTarget(null)} disabled={submitting}>
              Voltar
            </Button>
            <Button variant="danger" onClick={confirmCancelSession} isLoading={submitting}>
              Cancelar a data
            </Button>
          </>
        }
      >
        {cancelTarget && (
          <>
            <ConfirmText>
              Cancelar <strong>{formatSessionRange(cancelTarget.startsAt, cancelTarget.endsAt)}</strong>?
            </ConfirmText>
            <ConfirmText>
              {affectedPeople(cancelTarget) === 0 ? (
                'Ninguém reservou esta data, então ninguém será avisado.'
              ) : (
                <>
                  <strong>{formatNumber(affectedPeople(cancelTarget))} pessoas serão avisadas</strong>{' '}
                  ({formatNumber(cancelTarget.reservedCount)} com lugar garantido e{' '}
                  {formatNumber(cancelTarget.waitlistCount)} na fila de espera). As reservas delas
                  são canceladas.
                </>
              )}
            </ConfirmText>
            <ConfirmText>Quem já fez check-in continua registrado - presença não se desfaz.</ConfirmText>
          </>
        )}
      </Modal>

      {/* ------------------------------------------------ cancelar a série */}
      <Modal
        isOpen={cancelSeries !== null}
        onClose={() => setCancelSeries(null)}
        title="Cancelar a série inteira"
        width="520px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCancelSeries(null)} disabled={submitting}>
              Voltar
            </Button>
            <Button variant="danger" onClick={confirmCancelSeries} isLoading={submitting}>
              Cancelar a série
            </Button>
          </>
        }
      >
        {cancelSeries && (
          <>
            <ConfirmText>
              Esta série tem <strong>{formatNumber(seriesSessions.length)} datas futuras</strong>{' '}
              marcadas. Todas serão canceladas.
            </ConfirmText>
            <ConfirmText>
              {seriesAffected === 0 ? (
                'Nenhuma delas tem reserva, então ninguém será avisado.'
              ) : (
                <>
                  <strong>{formatNumber(seriesAffected)} pessoas serão avisadas</strong>, somando
                  reservas confirmadas e fila de espera das datas futuras.
                </>
              )}
            </ConfirmText>
            <ConfirmText>
              As datas que já aconteceram não são tocadas - cancelar o passado apagaria a lotação
              histórica de noites que existiram.
            </ConfirmText>
          </>
        )}
      </Modal>
    </Wrapper>
  );
};

export default ExperienceSessionsPanel;
